/**
 * ReadinessCard (P267.4) -- Home tile for admin OR approver, surfacing the
 * current OpsRes maturity index + tier and linking to the readiness view.
 * Exec-shaped (operators see nothing).
 */
import { useTranslation } from "react-i18next";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { canReadReadiness } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { BrassLink } from "shared/components/BrassLink";
import { Card } from "shared/components/Card";
import { useMaturityAssessment, useReadiness } from "shared/readiness/useReadiness";

const TIER_TONE: Record<string, string> = {
  A: "text-emerald-700",
  B: "text-sky-700",
  C: "text-amber-700",
  D: "text-orange-700",
  E: "text-rose-700",
};

export function ReadinessCard() {
  const { t } = useTranslation();
  const me = useMe();
  // A5: the Resilience Lead moves this number and could not see it.
  //
  // Round 9 E9 -- and the gate has to reach the QUERIES. This card hid
  // itself from a preparer and fired both reads anyway, so their home
  // took two 403s on every load for eight rounds. `me` has to have
  // settled before we can know, so hold until it has rather than firing
  // optimistically and apologising.
  const canSee = canReadReadiness(me.data);
  const mayAsk = me.isSuccess && canSee;
  const read = useReadiness(mayAsk);
  const assessment = useMaturityAssessment(mayAsk);
  if (!me.data || !canSee || !read.data) {
    return null;
  }
  const a = assessment.data ?? null;
  // Round 18 B18 -- with nothing admitted and no self-assessment there
  // is no maturity to show; say so rather than vanishing or suggesting.
  if (!a && read.data.not_suggested_reason) {
    return (
      <Card testid="readiness-card" className="space-y-2">
        <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
          <MarsadIcon name="benchmark" size={13} className="text-brand-brassDark" />
          {t("screens.readinessCard.weightedMaturityIndex")}
        </p>
        <p
          data-testid="readiness-card-not-suggested"
          className="text-[12px] text-ink-2"
        >
          {t("screens.readinessCard.notSuggested")}
        </p>
        <BrassLink
          to="/readiness"
          testid="readiness-card-open"
          className="inline-block"
        >
          {t("screens.readinessCard.openReadiness")}
        </BrassLink>
      </Card>
    );
  }
  const index = a?.set_index ?? read.data.suggested_index;
  const tier = a?.set_tier ?? read.data.tier_code ?? "E";
  // A card with no error boundary above it takes the whole home down if
  // it throws, so a payload missing the figure renders nothing rather
  // than calling toFixed on undefined.
  if (typeof index !== "number") {
    return null;
  }
  const self = a !== null;

  return (
    <Card testid="readiness-card" className="space-y-2">
      <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
        <MarsadIcon name="benchmark" size={13} className="text-brand-brassDark" />
        {/* CX round 12 A5 -- this card shows the WEIGHTED MATURITY
            INDEX, and said "Resilience readiness" over it. /readiness
            and /executive show the MARSAD Resilience Score and label
            it the same way, so on one afternoon the administrator's
            desk headlined 2.10 Tier D and the board's screen headlined
            40.7 tier C, ninety seconds apart. His stated success
            criterion is answering "are we set up yet" at a glance; he
            got two glances and two different answers.

            Named on its own face, in the words /readiness already
            uses. That page shows both together and labels them, which
            is the pattern and the only reason this was survivable. */}
        {t("screens.readinessCard.weightedMaturityIndex")}
      </p>
      <div className="flex items-baseline gap-2">
        <span
          data-testid="readiness-card-index"
          className="font-display text-2xl text-ink-1 tabular-nums"
        >
          {index.toFixed(2)}
        </span>
        <span className={`text-sm font-semibold ${TIER_TONE[tier] ?? TIER_TONE.E}`}>
          {/* No screen prints a tier without saying which measure
              produced it -- there are two, banded differently. */}
          {t("screens.readinessCard.maturityTier")} {tier}
        </span>
      </div>
      <p className="text-[12px] text-ink-2">
        {self
          ? t("screens.readinessCard.selfAssessedAgainstSama")
          : t("screens.readinessCard.suggestedFromLiveEvidence")}{" "}
        {t("screens.readinessCard.acrossThe10OpsresDimensions")}
      </p>
      <BrassLink to="/readiness" testid="readiness-card-open" className="inline-block">
        {t("screens.readinessCard.openReadiness")}
      </BrassLink>
    </Card>
  );
}
